/**
 * SMHI Open Data API Service
 *
 * Dokumentation:
 * - Prognoser: https://opendata.smhi.se/apidocs/metfcst/
 * - Varningar: https://opendata.smhi.se/apidocs/IBWwarnings/
 * 
 * Helt gratis, ingen API-nyckel krävs!
 * Licens: CC BY 4.0 - Ange SMHI som källa
 */

import type {
  LatLng,
  Result,
  WeatherForecast,
  WeatherWarning,
  WeatherSymbol,
  WarningLevel,
  WarningEvent,
} from '@/types';

const SMHI_FORECAST_BASE =
  'https://opendata-download-metfcst.smhi.se/api/category/pmp3g/version/2/geotype/point';

const SMHI_WARNINGS_URL =
  'https://opendata-download-warnings.smhi.se/ibww/api/version/1/warning.json';

/**
 * Hämta väderprognos för en specifik koordinat
 */
export async function fetchWeatherForecast(
  location: LatLng
): Promise<Result<WeatherForecast[]>> {
  try {
    const url = `${SMHI_FORECAST_BASE}/lon/${location.lon.toFixed(6)}/lat/${location.lat.toFixed(6)}/data.json`;
    const response = await fetch(url);

    if (!response.ok) {
      return {
        success: false,
        error: new Error(`SMHI API error: ${response.status} ${response.statusText}`),
      };
    }

    const data = await response.json();
    const forecasts = parseForecasts(data, location);

    return { success: true, data: forecasts };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Hämta alla aktiva vädervarningar
 */
export async function fetchWeatherWarnings(): Promise<Result<WeatherWarning[]>> {
  try {
    const response = await fetch(SMHI_WARNINGS_URL);

    if (!response.ok) {
      return {
        success: false,
        error: new Error(`SMHI Warnings API error: ${response.status}`),
      };
    }

    const data = await response.json();
    const warnings = parseWarnings(data);

    return { success: true, data: warnings };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Hämta väderprognos för en specifik tidpunkt
 */
export function getForecastForTime(
  forecasts: WeatherForecast[],
  targetTime: Date
): WeatherForecast | null {
  if (forecasts.length === 0) return null;

  let closest = forecasts[0];
  if (!closest) return null;
  
  let closestDiff = Math.abs(closest.validTime.getTime() - targetTime.getTime());

  for (const forecast of forecasts) {
    const diff = Math.abs(forecast.validTime.getTime() - targetTime.getTime());
    if (diff < closestDiff) {
      closest = forecast;
      closestDiff = diff;
    }
  }

  return closest;
}

interface WarningsFilterOptions {
  /** Exkludera havsvarningar (default: true för bilrutter) */
  excludeSeaWarnings?: boolean;
}

/**
 * Filtrera varningar som gäller för en specifik position och tid
 */
export function getWarningsForLocation(
  warnings: WeatherWarning[],
  location: LatLng,
  time: Date,
  options: WarningsFilterOptions = { excludeSeaWarnings: true }
): WeatherWarning[] {
  return warnings.filter((warning) => {
    // Filtrera bort havsvarningar för bilrutter
    if (options.excludeSeaWarnings && warning.isSeaWarning) {
      return false;
    }

    // Kolla tidsperiod
    if (time < warning.validFrom || time > warning.validTo) {
      return false;
    }

    // Kolla geografiskt område
    if (warning.area.polygon && warning.area.polygon.length > 0) {
      return isPointInPolygon(location, warning.area.polygon);
    }

    // Om ingen polygon finns, anta att varningen gäller brett
    return true;
  });
}

/**
 * Beräkna om det är dagsljus baserat på position och tid
 * Använder en förenklad soluppgång/solnedgång-beräkning
 */
export function isDaylight(location: LatLng, time: Date): boolean {
  const { sunrise, sunset } = getSunTimes(location, time);
  const timeMs = time.getTime();
  return timeMs >= sunrise.getTime() && timeMs <= sunset.getTime();
}

/**
 * Beräkna soluppgång och solnedgång för en position och datum
 * Baserat på NOAA:s algoritm (förenklad)
 */
function getSunTimes(location: LatLng, date: Date): { sunrise: Date; sunset: Date } {
  const dayOfYear = getDayOfYear(date);
  const lat = location.lat;

  // Solens deklination (förenklad)
  const declination = -23.45 * Math.cos((360 / 365) * (dayOfYear + 10) * (Math.PI / 180));

  // Timvinkel för soluppgång/solnedgång
  const latRad = lat * (Math.PI / 180);
  const decRad = declination * (Math.PI / 180);

  // Beräkna timvinkeln (när solen är vid horisonten)
  let cosHourAngle = -Math.tan(latRad) * Math.tan(decRad);

  // Hantera polarnatt och midnattssol
  if (cosHourAngle > 1) {
    // Polarnatt - solen går aldrig upp
    return {
      sunrise: new Date(date.setHours(12, 0, 0, 0)),
      sunset: new Date(date.setHours(12, 0, 0, 0)),
    };
  }
  if (cosHourAngle < -1) {
    // Midnattssol - solen går aldrig ner
    return {
      sunrise: new Date(date.setHours(0, 0, 0, 0)),
      sunset: new Date(date.setHours(23, 59, 59, 999)),
    };
  }

  const hourAngle = Math.acos(cosHourAngle) * (180 / Math.PI);

  // Soluppgång och solnedgång i timmar (lokal soltid)
  const solarNoon = 12; // Förenklat, ignorerar longitud-justering
  const sunriseHour = solarNoon - hourAngle / 15;
  const sunsetHour = solarNoon + hourAngle / 15;

  const sunrise = new Date(date);
  sunrise.setHours(Math.floor(sunriseHour), (sunriseHour % 1) * 60, 0, 0);

  const sunset = new Date(date);
  sunset.setHours(Math.floor(sunsetHour), (sunsetHour % 1) * 60, 0, 0);

  return { sunrise, sunset };
}

/**
 * Hämta dag på året (1-365/366)
 */
function getDayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

/**
 * Få vädersymbol som emoji
 * @param symbol SMHI vädersymbol (1-27)
 * @param location Position för att beräkna dag/natt (valfritt)
 * @param time Tid för att beräkna dag/natt (valfritt)
 */
export function getWeatherEmoji(
  symbol: WeatherSymbol,
  location?: LatLng,
  time?: Date
): string {
  // Kolla om det är natt
  const isNight = location && time ? !isDaylight(location, time) : false;

  // Dag-emojis
  const dayEmojis: Record<WeatherSymbol, string> = {
    1: '☀️',  // Klart
    2: '🌤️', // Lätt molnighet
    3: '⛅',  // Halvklart
    4: '🌥️', // Molnigt
    5: '☁️',  // Mycket molnigt
    6: '☁️',  // Mulet
    7: '🌫️', // Dimma
    8: '🌦️', // Lätt regnskur
    9: '🌧️', // Måttlig regnskur
    10: '🌧️', // Kraftig regnskur
    11: '⛈️', // Åska
    12: '🌨️', // Lätt snöblandat regn
    13: '🌨️', // Måttligt snöblandat regn
    14: '🌨️', // Kraftigt snöblandat regn
    15: '🌨️', // Lätt snöskur
    16: '❄️', // Måttlig snöskur
    17: '❄️', // Kraftig snöskur
    18: '🌧️', // Lätt regn
    19: '🌧️', // Måttligt regn
    20: '🌧️', // Kraftigt regn
    21: '⛈️', // Åska
    22: '🌨️', // Lätt snöblandat regn
    23: '🌨️', // Måttligt snöblandat regn
    24: '🌨️', // Kraftigt snöblandat regn
    25: '❄️', // Lätt snöfall
    26: '❄️', // Måttligt snöfall
    27: '❄️', // Kraftigt snöfall
  };

  // Natt-emojis (ersätter soliga symboler med måne/stjärnor)
  const nightEmojis: Record<WeatherSymbol, string> = {
    1: '🌙',  // Klart → Måne
    2: '🌙',  // Lätt molnighet → Måne med moln
    3: '☁️',  // Halvklart → Moln (månen syns inte så bra)
    4: '☁️',  // Molnigt
    5: '☁️',  // Mycket molnigt
    6: '☁️',  // Mulet
    7: '🌫️', // Dimma
    8: '🌧️', // Lätt regnskur
    9: '🌧️', // Måttlig regnskur
    10: '🌧️', // Kraftig regnskur
    11: '⛈️', // Åska
    12: '🌨️', // Lätt snöblandat regn
    13: '🌨️', // Måttligt snöblandat regn
    14: '🌨️', // Kraftigt snöblandat regn
    15: '🌨️', // Lätt snöskur
    16: '❄️', // Måttlig snöskur
    17: '❄️', // Kraftig snöskur
    18: '🌧️', // Lätt regn
    19: '🌧️', // Måttligt regn
    20: '🌧️', // Kraftigt regn
    21: '⛈️', // Åska
    22: '🌨️', // Lätt snöblandat regn
    23: '🌨️', // Måttligt snöblandat regn
    24: '🌨️', // Kraftigt snöblandat regn
    25: '❄️', // Lätt snöfall
    26: '❄️', // Måttligt snöfall
    27: '❄️', // Kraftigt snöfall
  };

  const emojis = isNight ? nightEmojis : dayEmojis;
  return emojis[symbol] || '🌡️';
}

// ============ Parsers ============

interface SMHIForecastResponse {
  timeSeries: Array<{
    validTime: string;
    parameters: Array<{
      name: string;
      values: number[];
    }>;
  }>;
}

function parseForecasts(data: SMHIForecastResponse, location: LatLng): WeatherForecast[] {
  return data.timeSeries.map((entry) => {
    const params = new Map<string, number>();

    for (const param of entry.parameters) {
      if (param.values[0] !== undefined) {
        params.set(param.name, param.values[0]);
      }
    }

    return {
      validTime: new Date(entry.validTime),
      location,
      temperature: params.get('t') ?? 0,
      windSpeed: params.get('ws') ?? 0,
      windDirection: params.get('wd') ?? 0,
      precipitation: params.get('pmean') ?? 0,
      cloudCover: params.get('tcc_mean') ?? 0,
      weatherSymbol: (params.get('Wsymb2') ?? 1) as WeatherSymbol,
      humidity: params.get('r') ?? 0,
    };
  });
}

interface SMHIWarningArea {
  id: number;
  approximateStart: string;
  approximateEnd?: string;
  areaName: { sv: string; en: string };
  warningLevel: { sv: string; en: string; code: string };
  eventDescription: { sv: string; en: string; code: string };
  descriptions: Array<{
    title: { sv: string; en: string; code: string };
    text: { sv: string; en: string };
  }>;
  area: {
    geometry: {
      type: string;
      coordinates: number[][][];
    };
  };
}

interface SMHIWarningItem {
  id: number;
  event: {
    code: string;
    sv: string;
    en: string;
    mhoClassification?: {
      code: string; // 'MET' = Meteorologi, 'OCE' = Oceanografi
      sv: string;
      en: string;
    };
  };
  warningAreas: SMHIWarningArea[];
}

type SMHIWarningResponse = SMHIWarningItem[];

function parseWarnings(data: SMHIWarningResponse): WeatherWarning[] {
  if (!Array.isArray(data)) return [];

  const warnings: WeatherWarning[] = [];

  // Varje item i arrayen är en varningstyp med flera warningAreas
  for (const item of data) {
    for (const area of item.warningAreas) {
      // Hitta beskrivningstexten (INCIDENT ger bäst info)
      const descText = area.descriptions.find((d) => d.title.code === 'INCIDENT');

      // Skapa unik ID för varje area-varning
      const uniqueId = `${item.id}-${area.id}`;

      // Beräkna validTo - om approximateEnd saknas, sätt till 24h framåt
      const validFrom = new Date(area.approximateStart);
      const validTo = area.approximateEnd
        ? new Date(area.approximateEnd)
        : new Date(validFrom.getTime() + 24 * 60 * 60 * 1000);

      // Identifiera havsvarningar baserat på kategori och event-kod
      const isSeaWarning = isSeaRelatedWarning(item.event.code, item.event.mhoClassification?.code);

      warnings.push({
        id: uniqueId,
        event: mapEventCode(item.event.code),
        level: area.warningLevel.code as WarningLevel,
        description: {
          sv: descText?.text.sv ?? area.eventDescription.sv ?? item.event.sv,
          en: descText?.text.en ?? area.eventDescription.en ?? item.event.en,
        },
        area: {
          name: area.areaName.sv ?? 'Okänt område',
          polygon: parsePolygon(area.area?.geometry?.coordinates),
        },
        validFrom,
        validTo,
        isSeaWarning,
      });
    }
  }

  return warnings;
}

/**
 * Identifiera om en varning gäller havsområden
 */
function isSeaRelatedWarning(eventCode: string, categoryCode?: string): boolean {
  // Oceanografi-varningar är alltid havsrelaterade
  if (categoryCode === 'OCE') return true;

  // Event-koder som slutar på _SEA är havsvarningar
  if (eventCode.endsWith('_SEA')) return true;

  // Specifika havsrelaterade event-koder
  const seaEventCodes = [
    'ICE_ACCRETION',    // Nedisning på fartyg
    'WIND_SEA',         // Vind till havs
    'STORM_SEA',        // Storm till havs
    'GALE',             // Kuling (oftast havs)
    'HIGH_WAVES',       // Höga vågor
  ];

  return seaEventCodes.includes(eventCode);
}

function mapEventCode(code: string): WarningEvent {
  const mapping: Record<string, WarningEvent> = {
    // Nya koder från ibww API v1
    RAIN: 'RAIN',
    RAIN_LAND: 'RAIN',
    SNOW: 'SNOW',
    SNOW_LAND: 'SNOW',
    WIND: 'WIND',
    WIND_LAND: 'WIND',
    WIND_SEA: 'WIND',
    GALE: 'WIND',
    STORM: 'STORM',
    STORM_SEA: 'STORM',
    THUNDER: 'THUNDERSTORM',
    THUNDERSTORM: 'THUNDERSTORM',
    ICE: 'ICE',
    ICE_LAND: 'ICE',
    ICE_ACCRETION: 'ICE',
    FIRE: 'FIRE_RISK',
    FIRE_RISK: 'FIRE_RISK',
    FOREST_FIRE: 'FIRE_RISK',
    HIGH_TEMP: 'HIGH_TEMPERATURE',
    HIGH_TEMPERATURE: 'HIGH_TEMPERATURE',
    HEAT: 'HIGH_TEMPERATURE',
    LOW_TEMP: 'LOW_TEMPERATURE',
    LOW_TEMPERATURE: 'LOW_TEMPERATURE',
    COLD: 'LOW_TEMPERATURE',
    FLOOD: 'FLOODING',
    FLOODING: 'FLOODING',
    HIGH_WATER_LEVEL: 'FLOODING',
    COAST_FLOOD: 'COASTAL_FLOODING',
    COASTAL_FLOODING: 'COASTAL_FLOODING',
    HIGH_WAVES: 'OTHER',
    SLIPPERY: 'ICE',
    AVALANCHE: 'OTHER',
  };

  return mapping[code] ?? 'OTHER';
}

function parsePolygon(coordinates?: number[][][]): LatLng[] | undefined {
  if (!coordinates || coordinates.length === 0) return undefined;

  const ring = coordinates[0];
  if (!ring) return undefined;

  return ring.map((coord) => ({
    lon: coord[0] ?? 0,
    lat: coord[1] ?? 0,
  }));
}

/**
 * Kontrollera om en punkt ligger inuti en polygon (ray casting)
 */
function isPointInPolygon(point: LatLng, polygon: LatLng[]): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  const x = point.lon;
  const y = point.lat;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]?.lon ?? 0;
    const yi = polygon[i]?.lat ?? 0;
    const xj = polygon[j]?.lon ?? 0;
    const yj = polygon[j]?.lat ?? 0;

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}

// ============ Hybrid Weather API ============

import { fetchOpenMeteoForecast } from './openmeteo';

/**
 * Kontrollera om en koordinat är inom SMHI:s täckningsområde (Sverige/Skandinavien)
 * SMHI täcker ungefär: lat 52-70, lon 2-32
 */
export function isInSMHICoverage(location: LatLng): boolean {
  const { lat, lon } = location;
  return lat >= 54 && lat <= 70 && lon >= 4 && lon <= 32;
}

/**
 * Hämta väderprognos - använder SMHI för Sverige, Open-Meteo för övriga Europa
 */
export async function fetchWeatherForecastHybrid(
  location: LatLng
): Promise<Result<WeatherForecast[]>> {
  if (isInSMHICoverage(location)) {
    // Försök med SMHI först för Sverige/Skandinavien
    const smhiResult = await fetchWeatherForecast(location);
    if (smhiResult.success) {
      return smhiResult;
    }
    // Om SMHI misslyckas, fallback till Open-Meteo
    console.warn('SMHI failed, falling back to Open-Meteo:', smhiResult.error);
  }

  // Använd Open-Meteo för resten av Europa eller som fallback
  return fetchOpenMeteoForecast(location);
}
